/** Backend collection config, cached once by TanStack Query for every consumer. */
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";

export interface Collection {
  id: string;
  name: string;
  icon: string;
  cohortId: string;
  cohortIds: string[];
  studyUids: Set<string>;
}

interface CollectionResponse {
  id: string;
  name: string;
  icon: string;
  cohort_id: string;
  cohort_ids: string[];
  study_uids: string[];
}

export const COLLECTIONS_QUERY_KEY = ["collections"] as const;
const EMPTY_COLLECTIONS = new Map<string, Collection>();

async function fetchCollections(): Promise<Map<string, Collection>> {
  const { data } = await axios.get<{ collections: CollectionResponse[] }>(
    `${getApiBaseUrl()}/api/config/collections`,
  );
  return new Map(
    data.collections.map((c) => {
      // The API supplies the complete cohort mapping. Never route an incomplete
      // collection to an unrelated hardcoded cohort or infer a replacement.
      if (
        !c.cohort_id ||
        !Array.isArray(c.cohort_ids) ||
        !c.cohort_ids.includes(c.cohort_id) ||
        !Array.isArray(c.study_uids)
      ) {
        throw new Error("Collection config is missing its cohort mapping or study list");
      }
      return [
        c.id,
        {
          id: c.id,
          name: c.name,
          icon: c.icon,
          cohortId: c.cohort_id,
          cohortIds: c.cohort_ids,
          studyUids: new Set(c.study_uids),
        },
      ];
    }),
  );
}

export function useCollections() {
  const query = useQuery({
    queryKey: COLLECTIONS_QUERY_KEY,
    queryFn: fetchCollections,
    staleTime: Infinity,
  });
  return { ...query, collections: query.data ?? EMPTY_COLLECTIONS };
}

/** Collection membership uses the same cached catalog as list navigation. */
export function useStudyCollectionIds(studyId: string, cohortId?: string): string[] {
  const { collections } = useCollections();
  const studyUid = studyId.split("::")[0];
  return Array.from(collections.values())
    .filter((c) => (!cohortId || c.cohortIds.includes(cohortId)) && c.studyUids.has(studyUid))
    .map((c) => c.id);
}
